import { APP_USE_LOCALE_FOR_DATE_TIME } from '@app/GlobalVariables/constants';
import { getGlobalVariable } from '@app/GlobalVariables/util';
import { Tooltip } from '@components/Tooltip';
import { DEBUG_STRING, hasLocalizationDebug } from '@utils/localize';
import { reportCustomError } from '@utils/reportCustomError';
import {
  DEPRECATEDGetDatetimeShort,
  DEPRECATEDUtcDatetimeToLocalTime,
  DEPRECATEDutcDatetimeToLocalDate,
  currentTZ,
  getTimeZone,
  getTimeZoneLabel,
} from '@utils/time';
import { win } from '@utils/win';
import { isValid } from 'date-fns';
import { isEqual, isNil } from 'lodash-es';
import { FC, HTMLAttributes, memo, useMemo } from 'react';
import { MergeExclusive } from 'type-fest';

interface BaseProps {
  /** Value is always a UTC time */
  value: Maybe<string | Date>;
  /** Example: "America/Chicago" */
  timezone?: Maybe<string>;
  /** Display tz like "CST" at end of string */
  showTimezone?: boolean;
  /** Fallback when there is no data present */
  noData?: string;
  /** Use a different html element instead of default */
  as?: string;
  className?: string;
  style?: HTMLAttributes<unknown>['style'];
  hideTimezoneTooltip?: boolean;
  /** Include seconds in output for timeOnly or full datetime (default false) */
  showSeconds?: boolean;
  /** Include milliseconds in output for timeOnly or full datetime (default false) */
  showMilliseconds?: boolean;
}

interface TimeOnly {
  timeOnly?: boolean;
}

interface DateOnly {
  dateOnly?: boolean;
}

interface OmitIfTimeOnly extends DateOnly {
  showYear?: boolean;
}

interface OmitTime {
  omitTime: true;
}

type TimeOnlyConsideration = MergeExclusive<OmitIfTimeOnly, TimeOnly>;
type OmitTimeConsideration = MergeExclusive<TimeOnly, OmitTime>;

interface OmitIfDateOnly extends TimeOnly, Pick<BaseProps, 'showTimezone'> {}

type DateOnlyConsideration = MergeExclusive<OmitIfDateOnly, DateOnly>;

export type Props = BaseProps &
  TimeOnlyConsideration &
  DateOnlyConsideration &
  OmitTimeConsideration;

/** Default format is MM/YY HH:MM for US Locale */
/** @deprecated - Use standard getDatetimeValue instead */
export const DEPRECATEDgetDatetimeValue = (props: Maybe<Props>): string => {
  if (!props) {
    return '';
  }
  const { value, timezone, noData, showTimezone } = props;
  const fallback = noData || '';
  try {
    if (isNil(value)) {
      return fallback;
    }
    let coercedDate: Date | undefined = undefined;
    coercedDate = new Date(value);
    if (!isValid(coercedDate)) {
      coercedDate = undefined;
    }
    if (!coercedDate) {
      return fallback;
    }
    if (props.timeOnly) {
      // TODO: it would be nice to emit a time with a timezone here instead
      if (!timezone) {
        return fallback;
      }
      return (
        DEPRECATEDUtcDatetimeToLocalTime(coercedDate, timezone) || fallback
      );
    } else if (props.dateOnly) {
      const res =
        DEPRECATEDutcDatetimeToLocalDate(
          coercedDate,
          timezone ? timezone : currentTZ,
          undefined,
          props.showYear,
          !timezone,
        ) ?? fallback;
      return props.showYear ? res.replace(/(.*)\/\d\d(\d\d)/, '$1/$2') : res;
    }
    return DEPRECATEDGetDatetimeShort(coercedDate, timezone ?? currentTZ, {
      showYear: props.showYear || false,
      showShortTZ: timezone ? showTimezone : true,
    });
  } catch (err: anyOk) {
    reportCustomError(err);
    return fallback;
  }
};

const shouldUseDebug = hasLocalizationDebug();

/** Default format is MM/YY HH:MM for US Locale, now supports user locale and always returns local format only */
export const getDatetimeValue = (props: Maybe<Props>): string => {
  const enableLocale = getGlobalVariable(APP_USE_LOCALE_FOR_DATE_TIME);
  if (!enableLocale) {
    return DEPRECATEDgetDatetimeValue(props);
  }
  if (shouldUseDebug) {
    return DEBUG_STRING;
  }
  if (!props) {
    return '';
  }
  const {
    dateOnly,
    noData,
    showTimezone,
    showYear,
    timeOnly,
    timezone,
    value,
    showSeconds,
    showMilliseconds,
  } = props;
  const fallback = noData || '';
  try {
    if (isNil(value)) {
      return fallback;
    }
    let coercedDate: Date | undefined = undefined;
    coercedDate = new Date(value);
    if (!isValid(coercedDate)) {
      coercedDate = undefined;
    }
    if (!coercedDate) {
      return fallback;
    }
    const userLocale = win.navigator.language || 'en-US';
    if (timeOnly) {
      if (!timezone) {
        return fallback;
      }
      // Only return time, no AM/PM (match legacy: always 24-hour format)
      return coercedDate
        .toLocaleTimeString(userLocale, {
          hour: '2-digit',
          minute: '2-digit',
          ...(showSeconds && { second: '2-digit' }),
          ...(showMilliseconds && { fractionalSecondDigits: 3 }),
          hour12: false, // force 24-hour format to match legacy
          timeZone: timezone,
        })
        .replace(/^24:/, '00:');
    }
    if (dateOnly) {
      const options: Intl.DateTimeFormatOptions = {
        month: '2-digit',
        day: '2-digit',
        ...(showYear && { year: '2-digit' }),
        timeZone: timezone ? timezone : currentTZ,
      };
      let formatted = coercedDate.toLocaleDateString(userLocale, options);
      // Patch: If no timezone, append local tz short label (from currentTZ)
      if (!timezone && showTimezone !== false) {
        const localTzShort = getTimeZone(coercedDate, currentTZ, 'short');
        formatted += ` ${localTzShort}`;
      }
      return formatted;
    }
    // Full datetime (date + time)
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      ...(showSeconds && { second: '2-digit' }),
      ...(showMilliseconds && { fractionalSecondDigits: 3 }),
      ...(showYear && { year: '2-digit' }),
      hour12: false,
      timeZone: timezone ? timezone : currentTZ,
    };
    let formatted = coercedDate
      .toLocaleString(userLocale, options)
      .replace(/,/g, '')
      .replace(/\s24:/, ' 00:')
      .trim();
    // Match legacy: append short timezone label if showTimezone or no timezone
    if (showTimezone || !timezone) {
      // Use only the short timezone label (e.g., 'CST') to match legacy output
      formatted += ` ${getTimeZone(
        coercedDate,
        timezone ?? currentTZ,
        'short',
      )}`;
    }
    return formatted;
  } catch (err: anyOk) {
    reportCustomError(err);
    return fallback;
  }
};

const DatetimeValueRaw: FC<Props> = (props) => {
  const { hideTimezoneTooltip = false } = props;
  const El = (props.as || 'span') as anyOk;

  const { longTimeZone, shortTimeZone } = useMemo(() => {
    const date = props?.value ? new Date(props?.value) : undefined;
    const timezone = props?.timezone || currentTZ;
    const longTZ = getTimeZone(date, timezone, 'long');
    const shortTZ = getTimeZone(date, timezone, 'short');
    return { longTimeZone: longTZ, shortTimeZone: shortTZ };
  }, [props.value, props.timezone]);

  const label = getTimeZoneLabel(longTimeZone, shortTimeZone);
  const datetimeValue = (
    <El className={props.className} style={props.style}>
      {getDatetimeValue(props)}
    </El>
  );
  if (label && !hideTimezoneTooltip && !props.dateOnly) {
    return (
      <Tooltip label={`${label}`} ariaLabel={`${label}`}>
        {datetimeValue}
      </Tooltip>
    );
  }
  return datetimeValue;
};

export const DatetimeValue = memo(DatetimeValueRaw, (prev, next) =>
  // we use isEqual here in case consumers pass in a Date for the value prop
  // Otherwise it will always bust the memo
  isEqual(prev, next),
) as typeof DatetimeValueRaw;
